import {
  Activity,
  Bone,
  Dumbbell,
  FileCheck2,
  ScanLine,
} from "lucide-react";
import { BoneGauge, CompositionDonut } from "./charts.jsx";
import { toFiniteNumber } from "../lib/dexaCalculations.js";
import deporteLogo from "../assets/deporte-logo.png?inline";
import paolaSignature from "../assets/firma-paola.png?inline";

function format(value, digits = 1, suffix = "") {
  const numeric = toFiniteNumber(value);
  return numeric !== null
    ? `${numeric.toLocaleString("es-CO", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })}${suffix}`
    : "Pendiente";
}

function kilograms(value) {
  const numeric = toFiniteNumber(value);
  return numeric === null ? null : numeric / 1000;
}

function formatTable(value, digits = 1, suffix = "") {
  return toFiniteNumber(value) === null ? "-" : format(value, digits, suffix);
}

function Citation({ sources, ids }) {
  const citations = ids
    .map((id) => {
      const index = sources.findIndex((source) => source.id === id);
      return index >= 0 ? { ...sources[index], number: index + 1 } : null;
    })
    .filter(Boolean);

  return citations.length ? (
    <sup className="dxa-citation" aria-label="Fuentes bibliográficas">
      {citations.map((source, index) => (
        <span key={source.id}>
          {index > 0 ? "," : null}
          <a href={source.url} target="_blank" rel="noreferrer" title={source.label}>
            {source.number}
          </a>
        </span>
      ))}
    </sup>
  ) : null;
}

function dateLabel(value) {
  if (!value) return "Fecha pendiente";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("es-CO", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

function sexLabel(value) {
  if (value === "female") return "Mujer";
  if (value === "male") return "Hombre";
  return "Pendiente";
}

function getVisual(record, role, key) {
  return record.source.pages
    ?.find((page) => page.role === role && page.visuals?.[key])
    ?.visuals?.[key] ?? "";
}

function getVisualFrom(record, candidates) {
  for (const [role, key] of candidates) {
    const visual = getVisual(record, role, key);
    if (visual) return visual;
  }
  return "";
}

function getHipVisual(record, kind) {
  const side = record.bone.hip.side === "left" ? "left" : "right";
  const suffix = kind === "scan" ? "Scan" : "Chart";
  return getVisualFrom(record, [
    ["hipBilateral", kind === "scan" ? `${side}Scan` : "referenceChart"],
    ["hip", kind === "scan" ? "scan" : "referenceChart"],
    ["hipDetail", `${side}${suffix}`],
  ]);
}

function ReportHeader({ record, page, totalPages = 4 }) {
  return (
    <header className={`dxa-report-header ${page > 1 ? "dxa-report-header--compact" : ""}`}>
      <div className="dxa-report-brand">
        <img src={deporteLogo} alt="Deporte · Ministerio del Deporte" />
        <div>
          <strong>Resultados de evaluación por DXA</strong>
          <small>Absorciometría dual de rayos X</small>
          <small className="dxa-report-author">Elaborado por Nutricionista Paola Hoyos</small>
        </div>
      </div>
      <div className="dxa-report-page-label">Página {page} de {totalPages}</div>
      <dl className="dxa-report-patient">
        <div className="dxa-report-patient__name">
          <dt>Paciente</dt>
          <dd>{record.patient.name || "Paciente"}</dd>
        </div>
        <div><dt>Fecha del examen</dt><dd>{dateLabel(record.exam.date)}</dd></div>
        <div><dt>Edad</dt><dd>{format(record.patient.age, 0, " años")}</dd></div>
        <div><dt>Sexo</dt><dd>{sexLabel(record.patient.sex)}</dd></div>
        <div><dt>Estatura</dt><dd>{format(record.patient.heightCm, 1, " cm")}</dd></div>
        <div><dt>Peso</dt><dd>{format(record.patient.scaleWeightKg, 1, " kg")}</dd></div>
        {record.patient.patientId ? <div><dt>Identificación</dt><dd>{record.patient.patientId}</dd></div> : null}
        <div><dt>Equipo</dt><dd>{[record.exam.vendor, record.exam.model].filter(Boolean).join(" · ") || "No identificado"}</dd></div>
      </dl>
    </header>
  );
}

function ReportFooter({ record, page, totalPages = 4 }) {
  return (
    <footer className="dxa-report-footer">
      <span>Informe DXA interpretado</span>
      <strong>{record.patient.name || "Paciente"}</strong>
      <span>{dateLabel(record.exam.date)} · {page}/{totalPages}</span>
    </footer>
  );
}

function SectionBand({ icon: Icon, eyebrow, title }) {
  return (
    <header className="dxa-section-band">
      <Icon aria-hidden="true" />
      <div>
        <span>{eyebrow}</span>
        <h2>{title}</h2>
      </div>
    </header>
  );
}

function DeviceVisual({
  src,
  title,
  alt,
  className = "",
  children,
}) {
  return (
    <figure className={`dxa-device-visual ${className}`}>
      <figcaption>{title}</figcaption>
      {src ? <img src={src} alt={alt} /> : <div className="dxa-device-visual__fallback">{children}</div>}
    </figure>
  );
}

function ResultCell({ label, value, tone = "neutral" }) {
  return (
    <div className={`dxa-result-cell dxa-result-cell--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function BmiSummary({ interpretation }) {
  const bmi = interpretation.bmi;
  let detail = "Pendiente por falta de peso o estatura.";
  if (bmi.mode === "adult") {
    detail = (
      <>
        Valor obtenido: <b>{format(bmi.value, 1, " kg/m²")}</b>; clasificación: <b>{bmi.classification}</b> según los puntos de corte para adultos de la OMS.
      </>
    );
  } else if (bmi.mode === "pediatric") {
    detail = (
      <>
        Valor obtenido: <b>{format(bmi.value, 1, " kg/m²")}</b>; en menores de 18 años se interpreta según IMC para la edad y el sexo (OMS).
      </>
    );
  } else if (bmi.mode === "age-required") {
    detail = (
      <>
        Valor obtenido: <b>{format(bmi.value, 1, " kg/m²")}</b>; se requiere confirmar la edad antes de aplicar la clasificación OMS.
      </>
    );
  }

  return (
    <div className="dxa-bmi-summary">
      <strong>Índice de masa corporal:</strong>
      <span>
        {detail}
        <Citation sources={interpretation.sources} ids={["who-bmi-adults"]} />
      </span>
    </div>
  );
}

function BodyFatReferenceTable({ record, interpretation }) {
  const rows = [
    ["general-younger", "Salud general", "< 40 años", "20-35%", "8-22%"],
    ["general-older", "Salud general", "≥ 40 años", "25-38%", "10-25%"],
    ["athlete-younger", "Deporte y fitness", "< 40 años", "16-28%", "5-15%"],
    ["athlete-older", "Deporte y fitness", "≥ 40 años", "20-33%", "7-18%"],
    ["underfat", "Grasa inadecuada", "Todas las edades", "< 12-14%", "< 2-5%"],
    ["obesity", "Obesidad", "< 40 / ≥ 40", "> 35 / > 38%", "> 22 / > 25%"],
  ];
  const sexColumn = record.patient.sex === "male" ? 4 : 3;
  return (
    <div className="dxa-body-fat-reference">
      <strong>
        Estándares de grasa corporal para DXA
        <Citation sources={interpretation.sources} ids={["acsm-body-composition"]} />
      </strong>
      <table>
        <thead><tr><th>Perfil</th><th>Edad</th><th>Mujeres</th><th>Hombres</th></tr></thead>
        <tbody>
          {rows.map(([id, label, ageLabel, women, men]) => {
            const current = id === interpretation.bodyComposition.referenceRow;
            return (
              <tr key={id} className={current ? "is-current" : ""}>
                <th>{label}</th>
                <td>{ageLabel}</td>
                <td>{current && sexColumn === 3 ? <span>{women}</span> : women}</td>
                <td>{current && sexColumn === 4 ? <span>{men}</span> : men}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <small>{interpretation.bodyComposition.classification.label}</small>
    </div>
  );
}

function VatReferenceScale({ value }) {
  const numeric = toFiniteNumber(value);
  const marker = numeric === null
    ? null
    : Math.max(0, Math.min(100, (numeric - 10) * 100 / 290));
  return (
    <div className="dxa-vat-scale" aria-label="Escala de área de grasa visceral">
      <div className="dxa-vat-scale__labels"><span>Normal</span><span>Aumentado</span><span>Alto</span></div>
      <div className="dxa-vat-scale__bar">
        <i />
        <i />
        <i />
        {marker !== null ? <b style={{ left: `${marker}%` }} title={`${format(numeric, 1, " cm²")}`} /> : null}
      </div>
      <div className="dxa-vat-scale__ticks"><span>10</span><span>100</span><span>160</span><span>300 cm²</span></div>
    </div>
  );
}

function CompositionRegionalTable({ record }) {
  const regions = record.composition.regions;
  const rows = [
    ["Brazo izquierdo", regions.leftArm],
    ["Brazo derecho", regions.rightArm],
    ["Tronco", regions.trunk],
    ["Pierna izquierda", regions.leftLeg],
    ["Pierna derecha", regions.rightLeg],
    ["Cabeza", regions.head],
    ["Total", record.composition.total],
  ];
  return (
    <div className="report-table-wrap">
      <table className="report-table report-table--dense">
        <colgroup>
          <col style={{ width: "27%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "12%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>Región</th>
            <th>Grasa</th>
            <th>Masa magra</th>
            <th>CMO</th>
            <th>Total</th>
            <th>Grasa %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, item]) => (
            <tr key={label}>
              <th>{label}</th>
              <td>{format(kilograms(item?.fatMassG), 3, " kg")}</td>
              <td>{format(kilograms(item?.leanMassG), 3, " kg")}</td>
              <td>{format(kilograms(item?.bmcG), 3, " kg")}</td>
              <td>{format(kilograms(item?.totalMassG), 3, " kg")}</td>
              <td>{format(item?.fatPercent, 1, "%")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdiposityTable({ indices }) {
  const rows = [
    ["Índice de masa grasa (IMG)", indices.fmi, "kg/m²", indices.fmiPercentileYoung, indices.fmiPercentileAgeMatched],
    ["Relación androide / ginoide", indices.androidGynoidRatio, "", null, null],
    ["Grasa tronco / piernas", indices.trunkLegFatRatio, "", indices.trunkLegFatPercentileYoung, indices.trunkLegFatPercentileAgeMatched],
    ["Grasa tronco / extremidades", indices.trunkExtremityFatRatio, "", indices.trunkExtremityFatPercentileYoung, indices.trunkExtremityFatPercentileAgeMatched],
    ["Tejido adiposo visceral - masa", indices.vatMassG, "g", null, null],
    ["Tejido adiposo visceral - volumen", indices.vatVolumeCm3, "cm³", null, null],
    ["Tejido adiposo visceral - área", indices.vatAreaCm2, "cm²", null, null],
  ];
  return (
    <div className="report-table-wrap">
      <table className="report-table report-table--dense">
        <colgroup><col style={{ width: "50%" }} /><col style={{ width: "24%" }} /><col style={{ width: "13%" }} /><col style={{ width: "13%" }} /></colgroup>
        <thead><tr><th>Índice adiposo</th><th>Resultado</th><th>Percentil GN</th><th>Percentil EC</th></tr></thead>
        <tbody>
          {rows.map(([label, value, unit, general, ageMatched]) => (
            <tr key={label}>
              <th>{label}</th>
              <td>{format(value, unit === "g" ? 0 : 2, unit ? ` ${unit}` : "")}</td>
              <td>{formatTable(general, 0)}</td>
              <td>{formatTable(ageMatched, 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MuscleTable({ indices }) {
  const rows = [
    ["Índice de masa magra / estatura² (IMM)", indices.lmi, indices.lmiPercentileYoung, indices.lmiPercentileAgeMatched],
    ["Índice de masa magra apendicular / estatura² (IMMA)", indices.almi, indices.almiPercentileYoung, indices.almiPercentileAgeMatched],
  ];
  return (
    <div className="report-table-wrap">
      <table className="report-table">
        <colgroup>
          <col style={{ width: "49%" }} />
          <col style={{ width: "21%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "15%" }} />
        </colgroup>
        <thead><tr><th>Índice de desarrollo muscular</th><th>Resultado</th><th>Percentil GN</th><th>Percentil EC</th></tr></thead>
        <tbody>
          {rows.map(([label, value, young, ageMatched]) => (
            <tr key={label}>
              <th>{label}</th>
              <td>{formatTable(value, 2, " kg/m²")}</td>
              <td>{formatTable(young, 0)}</td>
              <td>{formatTable(ageMatched, 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WholeBodyBmdTable({ record, interpretation }) {
  const regions = record.bone.wholeBodyRegions;
  const rows = [
    ["leftArm", "Brazo izquierdo", regions.leftArm],
    ["rightArm", "Brazo derecho", regions.rightArm],
    ["leftRibs", "Costillas izquierdas", regions.leftRibs],
    ["rightRibs", "Costillas derechas", regions.rightRibs],
    ["thoracicSpine", "Columna dorsal", regions.thoracicSpine],
    ["lumbarSpine", "Columna lumbar", regions.lumbarSpine],
    ["pelvis", "Pelvis", regions.pelvis],
    ["leftLeg", "Pierna izquierda", regions.leftLeg],
    ["rightLeg", "Pierna derecha", regions.rightLeg],
    ["head", "Cabeza", regions.head],
    ["total", "Total", record.bone.wholeBody],
  ];
  const statuses = new Map(interpretation.bone.regionalRows.map((row) => [row.key, row]));
  return (
    <div className="report-table-wrap">
      <table className="report-table report-table--dense">
        <colgroup>
          <col style={{ width: "31%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "23%" }} />
        </colgroup>
        <thead><tr><th>Región</th><th>Área cm²</th><th>CMO g</th><th>DMO g/cm²</th><th>Interpretación</th></tr></thead>
        <tbody>
          {rows.map(([key, label, item]) => {
            const status = key === "total"
              ? {
                status: interpretation.bone.wholeBodyClassification.id === "below" ? "attention" : "favorable",
                classification: interpretation.bone.wholeBodyClassification.label,
              }
              : statuses.get(key);
            return (
            <tr key={label} className={status ? `dxa-bmd-row--${status.status}` : ""}>
              <th>{label}</th>
              <td>{format(item?.areaCm2, 2)}</td>
              <td>{format(item?.bmcG, 2)}</td>
              <td>{format(item?.bmd, 3)}</td>
              <td>{status?.classification ?? "Dato descriptivo"}</td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CentralBmdTable({ rows }) {
  return (
    <div className="report-table-wrap">
      <table className="report-table report-table--dense">
        <colgroup>
          <col style={{ width: "21%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "10.5%" }} />
          <col style={{ width: "10.5%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>Región</th>
            <th>Área cm²</th>
            <th>CMO g</th>
            <th>DMO g/cm²</th>
            <th title="T-score">T</th>
            <th title="Z-score">Z</th>
            <th>RM %</th>
            <th>EC %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, item]) => (
            <tr key={label}>
              <th>{label}</th>
              <td>{formatTable(item?.areaCm2, 2)}</td>
              <td>{formatTable(item?.bmcG, 2)}</td>
              <td>{formatTable(item?.bmd, 3)}</td>
              <td>{formatTable(item?.tScore, 1)}</td>
              <td>{formatTable(item?.zScore, 1)}</td>
              <td>{formatTable(item?.referenceMeanPercent, 0)}</td>
              <td>{formatTable(item?.ageMatchedPercent, 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InterpretationBox({ title, children, tone = "green" }) {
  return (
    <div className={`dxa-interpretation-box dxa-interpretation-box--${tone}`}>
      <strong>{title}</strong>
      <p>{children}</p>
    </div>
  );
}

export function PatientReport({ interpretation, includeOriginalPages = false }) {
  const { record } = interpretation;
  const total = record.composition.total;
  const indices = record.composition.indices;
  const sourcePages = record.source.pages?.filter((page) => page.imageUrl) ?? [];
  const compositionScan = getVisual(record, "composition", "scan");
  const bodyFatChart = getVisual(record, "composition", "bodyFatChart");
  const wholeBodyScan = getVisual(record, "wholeBodyBmd", "scan");
  const wholeBodyChart = getVisual(record, "wholeBodyBmd", "referenceChart");
  const hipScan = getHipVisual(record, "scan");
  const hipChart = getHipVisual(record, "chart");
  const lumbarScan = getVisualFrom(record, [["lumbar", "scan"], ["lumbarSummary", "scan"]]);
  const lumbarChart = getVisualFrom(record, [["lumbarSummary", "referenceChart"], ["lumbar", "referenceChart"]]);
  const modeLabel = interpretation.bone.mode === "t" ? "T-score" : "Z-score";
  const hipRows = [
    ["Cuello femoral", record.bone.hip.neck],
    ["Cadera total", record.bone.hip.total],
  ];
  const lumbarRows = [
    ["L1", record.bone.lumbar.l1],
    ["L2", record.bone.lumbar.l2],
    ["L3", record.bone.lumbar.l3],
    ["L4", record.bone.lumbar.l4],
    ["L1-L4", record.bone.lumbar.total],
  ];

  return (
    <article id="patient-report" className="patient-report dxa-patient-report">
      <section className="report-page dxa-report-page dxa-report-page--composition">
        <ReportHeader record={record} page={1} />
        <BmiSummary interpretation={interpretation} />

        <section className="dxa-report-section">
          <SectionBand icon={ScanLine} eyebrow="Cuantificación del tejido corporal total" title="Composición corporal" />
          <div className="dxa-composition-overview">
            <DeviceVisual
              src={compositionScan}
              title="Escaneo de composición corporal"
              alt="Escaneo DXA de composición corporal del paciente"
              className="dxa-device-visual--body"
            >
              <CompositionDonut total={total} />
            </DeviceVisual>
            <div className="dxa-primary-results">
              <ResultCell label="Grasa corporal" value={format(total.fatPercent, 1, "%")} tone="orange" />
              <ResultCell label="Masa total DXA" value={format(kilograms(total.totalMassG), 1, " kg")} />
              <ResultCell label="Tejido graso" value={format(kilograms(total.fatMassG), 1, " kg")} tone="yellow" />
              <ResultCell label="Tejido magro" value={format(kilograms(total.leanMassG), 1, " kg")} tone="rose" />
              <ResultCell label="Contenido mineral óseo" value={format(kilograms(total.bmcG), 2, " kg")} tone="blue" />
            </div>
            <div className="dxa-fat-reference-column">
              <DeviceVisual
                src={bodyFatChart}
                title="Comparación de grasa corporal según edad"
                alt="Gráfica del equipo para porcentaje de grasa corporal"
                className="dxa-device-visual--chart"
              >
                <div className="dxa-fallback-result">
                  <strong>{format(total.fatPercent, 1, "%")}</strong>
                  <span>{interpretation.bodyComposition.classification.label}</span>
                </div>
              </DeviceVisual>
              <BodyFatReferenceTable record={record} interpretation={interpretation} />
            </div>
          </div>
          <div className="dxa-composition-legend" aria-label="Código de colores del escaneo">
            <span>Grasa</span><i /><span>Masa magra</span><i /><span>Hueso</span>
          </div>
          <div className="dxa-composition-insights">
            <InterpretationBox title="Lectura del escaneo corporal">
              {interpretation.bodyComposition.scanSummary}
            </InterpretationBox>
            <InterpretationBox
              title={interpretation.bodyComposition.classification.label}
              tone={interpretation.headline.fat.status === "favorable" ? "green" : "orange"}
            >
              {interpretation.bodyComposition.comparison}
            </InterpretationBox>
          </div>
        </section>

        <section className="dxa-report-section dxa-report-section--compact">
          <SectionBand icon={Activity} eyebrow="Regiones de interés" title="Composición corporal regional" />
          <div className="dxa-regional-layout">
            <CompositionRegionalTable record={record} />
            <div className="dxa-regional-notes">
              <InterpretationBox title={interpretation.symmetry.arms.title}>
                {interpretation.symmetry.arms.text}
              </InterpretationBox>
              <InterpretationBox title={interpretation.symmetry.legs.title}>
                {interpretation.symmetry.legs.text}
              </InterpretationBox>
            </div>
          </div>
        </section>

        <ReportFooter record={record} page={1} />
      </section>

      <section className="report-page dxa-report-page dxa-report-page--indices">
        <ReportHeader record={record} page={2} />

        <section className="dxa-report-section dxa-report-section--compact dxa-report-section--adiposity">
          <SectionBand icon={Activity} eyebrow="Distribución del tejido graso" title="Índices adiposos" />
          <div className="dxa-adiposity-report-grid">
            <div className="dxa-adiposity-indices">
              <AdiposityTable indices={indices} />
            </div>
            <div className="dxa-adiposity-context">
              <aside className="dxa-patient-education">
                <strong>Para comprender la distribución de la grasa corporal</strong>
                <p>
                  {interpretation.adiposity.education}
                  <Citation
                    sources={interpretation.sources}
                    ids={["nhanes-total-2009", "nana-dxa-athletes-2015", "android-gynoid-lipids-2015", "ofenheimer-vat-2020"]}
                  />
                </p>
              </aside>
              <InterpretationBox
                title="Masa grasa y distribución"
                tone={["attention", "review"].some((status) => [interpretation.adiposity.fmi.status, interpretation.adiposity.trunkExtremity.status].includes(status)) ? "orange" : "green"}
              >
                {interpretation.adiposity.summary || "Datos pendientes para completar la interpretación."}
                <Citation sources={interpretation.sources} ids={["messina-dxa-2020", "nhanes-total-2009"]} />
              </InterpretationBox>
            </div>
            <div className="dxa-adiposity-visceral">
              <InterpretationBox
                title="Tejido adiposo visceral: área y masa"
                tone={interpretation.adiposity.vat.status === "favorable" ? "green" : "orange"}
              >
                {interpretation.adiposity.vat.note}
                <Citation sources={interpretation.sources} ids={["ofenheimer-vat-2020", "messina-dxa-2020"]} />
              </InterpretationBox>
              <VatReferenceScale value={indices.vatAreaCm2} />
            </div>
          </div>
        </section>

        <section className="dxa-report-section dxa-report-section--compact">
          <SectionBand icon={Dumbbell} eyebrow="Índices de desarrollo muscular" title="Tejido magro" />
          <div className="dxa-indices-layout dxa-indices-layout--muscle">
            <MuscleTable indices={indices} />
            <div className="dxa-muscle-interpretations">
              <InterpretationBox
                title={interpretation.muscle.imm.title}
                tone={interpretation.muscle.imm.status === "favorable" ? "green" : "orange"}
              >
                {interpretation.muscle.imm.note}
                <Citation sources={interpretation.sources} ids={["nhanes-total-2009", "nhanes-regional-2017", "messina-dxa-2020"]} />
              </InterpretationBox>
              <InterpretationBox
                title={interpretation.muscle.imma.title}
                tone={interpretation.muscle.imma.status === "favorable" ? "green" : "orange"}
              >
                {interpretation.muscle.imma.note} {interpretation.muscle.note}
                <Citation sources={interpretation.sources} ids={["nhanes-total-2009", "nhanes-regional-2017", "messina-dxa-2020", "ewgsop2"]} />
              </InterpretationBox>
            </div>
          </div>
        </section>

        <ReportFooter record={record} page={2} />
      </section>

      <section className="report-page dxa-report-page dxa-report-page--bone-overview">
        <ReportHeader record={record} page={3} />

        <section className="dxa-report-section">
          <SectionBand icon={Bone} eyebrow="Cuerpo entero" title="Densidad mineral ósea por regiones" />
          <div className="dxa-bone-overview">
            <DeviceVisual
              src={wholeBodyScan}
              title="Escaneo óseo de cuerpo entero"
              alt="Escaneo DXA de densidad mineral ósea de cuerpo entero"
              className="dxa-device-visual--bone"
            >
              <Bone aria-hidden="true" />
            </DeviceVisual>
            <WholeBodyBmdTable record={record} interpretation={interpretation} />
            <div className="dxa-bone-chart-column">
              <DeviceVisual
                src={wholeBodyChart}
                title="Percentiles por edad y sexo"
                alt="Gráfica del equipo para densidad mineral ósea de cuerpo entero"
                className="dxa-device-visual--bone-chart"
              >
                <BoneGauge score={interpretation.bone.score} mode={interpretation.bone.mode === "t" ? "t" : "z"} />
              </DeviceVisual>
              <ResultCell
                label={`DMO cuerpo entero · ${modeLabel}`}
                value={`${format(record.bone.wholeBody.bmd, 3, " g/cm²")} · ${format(
                  interpretation.bone.mode === "t" ? record.bone.wholeBody.tScore : record.bone.wholeBody.zScore,
                  1,
                )}`}
                tone="blue"
              />
            </div>
          </div>
          <InterpretationBox
            title={interpretation.bone.wholeBodyClassification.label}
            tone={interpretation.bone.regionalRows.some((row) => row.status === "attention") ? "orange" : "green"}
          >
            {interpretation.bone.wholeBodyScore.text}
            {" "}La referencia operativa regional del formato es {format(interpretation.bone.regionalReference[0], 2)}-{format(interpretation.bone.regionalReference[1], 2, " g/cm²")}.
            {` ${interpretation.bone.regionalRecommendation}`}
            <Citation sources={interpretation.sources} ids={["exercise-bone-consensus"]} />
          </InterpretationBox>
        </section>

        <section className="dxa-report-section">
          <SectionBand icon={Bone} eyebrow={`Cadera ${record.bone.hip.side === "right" ? "derecha" : "izquierda"}`} title="Densidad mineral ósea de cadera" />
          <div className="dxa-site-layout">
            <DeviceVisual
              src={hipScan}
              title="Escaneo de cadera"
              alt="Escaneo DXA de cadera"
              className="dxa-device-visual--site"
            >
              <Bone aria-hidden="true" />
            </DeviceVisual>
            <div className="dxa-site-data">
              <CentralBmdTable rows={hipRows} />
              <InterpretationBox title={toFiniteNumber(
                interpretation.bone.mode === "z"
                  ? record.bone.hip.total.zScore
                  : record.bone.hip.total.tScore,
              ) === null
                ? "Cadera total · lectura descriptiva"
                : `Cadera total · ${modeLabel} ${format(
                  interpretation.bone.mode === "z"
                    ? record.bone.hip.total.zScore
                    : record.bone.hip.total.tScore,
                  1,
                )}`}>
                {interpretation.bone.hip.text}
              </InterpretationBox>
            </div>
            <DeviceVisual
              src={hipChart}
              title="Percentiles por edad y sexo"
              alt="Gráfica del equipo para densidad mineral ósea de cadera"
              className="dxa-device-visual--site-chart"
            >
              <BoneGauge
                score={interpretation.bone.mode === "z" ? record.bone.hip.total.zScore : record.bone.hip.total.tScore}
                mode={interpretation.bone.mode === "t" ? "t" : "z"}
              />
            </DeviceVisual>
          </div>
        </section>

        <ReportFooter record={record} page={3} />
      </section>

      <section className="report-page dxa-report-page dxa-report-page--bone">
        <ReportHeader record={record} page={4} />

        <section className="dxa-report-section">
          <SectionBand icon={Bone} eyebrow="Columna AP lumbar" title="Densidad mineral ósea de columna L1-L4" />
          <div className="dxa-lumbar-layout">
            <DeviceVisual
              src={lumbarScan}
              title="Escaneo de columna lumbar"
              alt="Escaneo DXA de columna lumbar L1 a L4"
              className="dxa-device-visual--lumbar"
            >
              <Bone aria-hidden="true" />
            </DeviceVisual>
            <div className="dxa-lumbar-data">
              <CentralBmdTable rows={lumbarRows} />
              <ResultCell label="DMO total L1-L4" value={format(record.bone.lumbar.total.bmd, 3, " g/cm²")} tone="blue" />
            </div>
            <DeviceVisual
              src={lumbarChart}
              title="Percentiles por edad y sexo"
              alt="Gráfica del equipo para densidad mineral ósea de columna lumbar"
              className="dxa-device-visual--lumbar-chart"
            >
              <BoneGauge
                score={interpretation.bone.mode === "z"
                  ? record.bone.lumbar.total.zScore
                  : record.bone.lumbar.total.tScore}
                mode={interpretation.bone.mode === "t" ? "t" : "z"}
              />
            </DeviceVisual>
          </div>
          <div className="dxa-lumbar-interpretations">
            <InterpretationBox title={interpretation.bone.lumbar.title}>
              {interpretation.bone.lumbar.text}
            </InterpretationBox>
            <InterpretationBox title={interpretation.bone.title} tone={interpretation.bone.status === "favorable" ? "green" : "orange"}>
              {interpretation.bone.text}
            </InterpretationBox>
          </div>
        </section>

        <section className="dxa-report-section dxa-report-section--conclusion">
          <SectionBand icon={FileCheck2} eyebrow="Lectura integrada del examen" title="Conclusión interpretativa" />
          <div className="dxa-final-conclusion">
            <FileCheck2 aria-hidden="true" />
            <p>{interpretation.conclusion}</p>
          </div>
          <div className="dxa-exam-summary">
            <ResultCell label="Grasa corporal" value={format(total.fatPercent, 1, "%")} tone="orange" />
            <ResultCell label="IMMA" value={format(indices.almi, 2, " kg/m²")} tone="green" />
            <ResultCell label="VAT" value={format(indices.vatAreaCm2, 1, " cm²")} tone="yellow" />
            <ResultCell label="DMO cadera" value={format(record.bone.hip.total.bmd, 3, " g/cm²")} tone="blue" />
            <ResultCell label="DMO L1-L4" value={format(record.bone.lumbar.total.bmd, 3, " g/cm²")} tone="blue" />
          </div>
          <div className="dxa-professional-approval">
            <img src={paolaSignature} alt="Firma de la profesional responsable" />
            <span />
            <strong>Profesional: {record.professional.approvedBy || "Nutricionista Paola Hoyos"}</strong>
            <small>{record.professional.organization || "Centro de Ciencias del Deporte · Ministerio del Deporte"}</small>
            {record.professional.license ? <small>{record.professional.license}</small> : null}
          </div>
        </section>
        <section className="dxa-report-references dxa-report-references--compact">
          <strong>Referencias bibliográficas</strong>
          <p>Fuentes consultadas para las referencias de composición corporal, percentiles, lectura densitométrica y recomendaciones del informe.</p>
          <ol>
            {interpretation.sources.filter((source) => source.apa).map((source) => (
              <li key={source.id} id={`reference-${source.id}`}>
                <a href={source.url} target="_blank" rel="noreferrer" title={`Abrir ${source.label}`}>
                  {source.apa}
                </a>
              </li>
            ))}
          </ol>
        </section>
        <ReportFooter record={record} page={4} />
      </section>

      {includeOriginalPages && sourcePages.length ? (
        <section className="report-original-pages">
          {sourcePages.map((page) => (
            <figure className="report-page report-original-page" key={page.pageNumber}>
              <figcaption>Anexo · página original {page.pageNumber} del equipo</figcaption>
              <img src={page.imageUrl} alt={`Página original ${page.pageNumber} del informe DXA`} />
            </figure>
          ))}
        </section>
      ) : null}
    </article>
  );
}
